<template>
    <div class="timetable-dates">
        <VBtn @click="setPrevDate" class="timetable-dates__prev" :disabled="activeDate <= minDate">
            <SvgIcon icon="chevron-left" width="1.25rem" height="1.25rem" />
        </VBtn>
        <ul class="timetable-dates__list">
            <li v-for="(date, index) in visibleDates" :key="index">
                <VRadio
                    v-model="activeDate"
                    :value="date"
                    :id="`date-${YYYYMMDD(date)}`"
                    :disabled="date < minDate"
                    name="timetable-date"
                    class="timetable-dates__date"
                >
                    <div class="date-text weekday">{{ dayOfWeekName(date, 3) }}</div>
                    <div class="date-text day">{{ dayOrdial(date) }}</div>
                    <div class="date-text month">{{ monthName(date, 3) }}</div>
                </VRadio>
            </li>
        </ul>
        <VBtn @click="setNextDate" class="timetable-dates__next">
            <SvgIcon icon="chevron-right" width="1.25rem" height="1.25rem" />
        </VBtn>
    </div>
</template>

<script lang="ts" setup>
import SvgIcon from "@/components/icons/SvgIcon.vue";
import { dayOfWeekName, dayOrdial, monthName, YYYYMMDD } from "@/helpers/dateHelpers";
import { useMediaQuery } from "@vueuse/core";
import { addDays, eachDayOfInterval, startOfToday } from "date-fns";
import { VBtn, VRadio } from "v-ferry-components";
import { computed, ref, watch } from "vue";

const minDate = ref<Date>(startOfToday());
const activeDate = ref<Date>(startOfToday());

const isMediumScreen = useMediaQuery("(min-width: 720px)");
const isLargeScreen = useMediaQuery("(min-width: 1024px)");

const visibleDates = computed(() => {
    if (isLargeScreen.value) {
        return eachDayOfInterval({
            start: addDays(activeDate.value, -2),
            end: addDays(activeDate.value, 4),
        });
    }

    if (isMediumScreen.value) {
        return eachDayOfInterval({
            start: addDays(activeDate.value, -1),
            end: addDays(activeDate.value, 1),
        });
    }

    return [activeDate.value];
});

const setPrevDate = () => {
    activeDate.value = addDays(activeDate.value, -1);
};

const setNextDate = () => {
    activeDate.value = addDays(activeDate.value, 1);
};

const emit = defineEmits(["selectedDate"]);

watch(activeDate, (value) => {
    emit("selectedDate", value);
});
</script>

<style lang="scss" scoped>
.timetable-dates {
    $prefix: ".timetable-dates";

    display: flex;
    margin-top: 3.75rem;
    box-shadow: var(--box-shadow);

    @media print {
        box-shadow: none;
    }

    &__list {
        display: flex;
        flex: auto;
        column-gap: 1px;
        padding: 0;
        margin: 0;
        background-color: #d8d8d8;

        li {
            flex: 0 1 100%;
        }
    }

    &__date {
        display: flex;
        height: 100%;
        width: 100%;
        border-radius: 0;

        background-color: var(--c-base);

        :deep(input) {
            &:checked {
                + label {
                    @include media-breakpoint-up(sm) {
                        z-index: 1;
                        outline: 2px solid var(--c-primary);
                        height: calc(100% + 20px);
                        background-color: var(--c-primary);
                        color: var(--c-base);
                        border-radius: var(--border-radius);
    
                        .date-text {
                            color: var(--c-base);
                            font-size: 1.125rem;
                        }
                    }

                    &::before,
                    &::after {
                        content: none;
                    }
                }
            }

            + label {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: 0;
                width: 100%;
                height: 100%;

                &::before,
                &::after {
                    content: none;
                }
            }
        }

        .date-text {
            color: var(--c-primary);

            &.day,
            &.month {
                font-weight: bold;
            }

            &.weekday {
                font-weight: 400;
            }
        }

        &.disabled {
            opacity: 0.5;
        }
    }

    &__prev,
    &__next {
        padding: 0;
        width: 40px;
        height: 100px;

        :deep(.v-btn__content) {
            display: flex;
        }
    }

    &__prev {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    &__next {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
}
</style>
